import { useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Music2, Trash2, UploadCloud, X, CheckCircle2, AlertCircle, Pencil, Search, Play } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { BACKEND_URL } from '../../lib/backend';

const GENRES = [
  { value: '', label: 'Unspecified' },
  { value: 'dancehall', label: 'Dancehall' },
  { value: 'reggae', label: 'Reggae' },
  { value: 'soca', label: 'Soca' },
  { value: 'afrobeats', label: 'Afrobeats' },
  { value: 'roots', label: 'Roots' },
  { value: 'trap_dancehall', label: 'Trap Dancehall' },
];

const genreLabel = (v) => GENRES.find(g => g.value === v)?.label || (v || '—');
const stripExt = (name) => name.replace(/\.[^.]+$/, '');

function fmtSize(bytes) {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
}

function GenreSelect({ value, onChange, testid }) {
  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="input-dark h-9 text-xs px-2 rounded-md w-full"
      data-testid={testid}
    >
      {GENRES.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
    </select>
  );
}

export function StudioLibraryTab() {
  const [instrumentals, setInstrumentals] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [genreFilter, setGenreFilter] = useState('all');

  const [batchDefaults, setBatchDefaults] = useState({ genre: '', bpm: '', musical_key: '', tags: '' });
  const [queue, setQueue] = useState([]);
  const [uploadingAll, setUploadingAll] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileRef = useRef(null);

  const [editingInst, setEditingInst] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', genre: '', bpm: '', musical_key: '', tags: '' });
  const [savingEdit, setSavingEdit] = useState(false);

  const load = async () => {
    try {
      const res = await api.adminGetInstrumentals();
      setInstrumentals(res.data || []);
    } catch (e) {
      toast.error('Failed to load instrumentals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const enqueue = (files) => {
    if (files.length === 0) return;
    setQueue((prev) => [
      ...prev,
      ...files.map((file) => ({
        fileId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        title: stripExt(file.name),
        genre: batchDefaults.genre,
        bpm: batchDefaults.bpm,
        musical_key: batchDefaults.musical_key,
        tags: batchDefaults.tags,
        progress: 0,
        status: 'queued', // queued | uploading | done | error
        error: '',
      })),
    ]);
  };

  const removeFromQueue = (fileId) => setQueue((q) => q.filter((i) => i.fileId !== fileId));

  const updateQueueItem = (fileId, updates) =>
    setQueue((q) => q.map((i) => (i.fileId === fileId ? { ...i, ...updates } : i)));

  const applyBatchToAll = () => {
    setQueue((q) => q.map((i) => (
      i.status === 'queued'
        ? {
            ...i,
            genre: batchDefaults.genre || i.genre,
            bpm: batchDefaults.bpm || i.bpm,
            musical_key: batchDefaults.musical_key || i.musical_key,
            tags: batchDefaults.tags || i.tags,
          }
        : i
    )));
    toast.success('Batch defaults applied to queued files');
  };

  const clearFinished = () => setQueue((q) => q.filter((i) => i.status !== 'done'));

  const handleFiles = (list) => {
    const files = Array.from(list || []).filter((f) => f.type.startsWith('audio/'));
    if (list && files.length !== list.length) {
      toast.error('Non-audio files were skipped');
    }
    enqueue(files);
  };

  const handlePick = (e) => {
    handleFiles(e.target.files);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    handleFiles(e.dataTransfer.files);
  };

  const uploadOne = async (item) => {
    updateQueueItem(item.fileId, { status: 'uploading', progress: 0, error: '' });
    try {
      const fd = new FormData();
      fd.append('file', item.file);
      fd.append('title', (item.title || stripExt(item.file.name)).slice(0, 120));
      if (item.genre) fd.append('genre', item.genre);
      if (item.bpm) fd.append('bpm', String(item.bpm));
      if (item.musical_key) fd.append('musical_key', item.musical_key);
      if (item.tags) fd.append('tags', item.tags);
      await api.adminUploadInstrumental(fd, (evt) => {
        if (evt.total) {
          const pct = Math.round((evt.loaded / evt.total) * 100);
          updateQueueItem(item.fileId, { progress: pct });
        }
      });
      updateQueueItem(item.fileId, { status: 'done', progress: 100 });
      return true;
    } catch (err) {
      updateQueueItem(item.fileId, {
        status: 'error',
        error: err.response?.data?.detail || 'Upload failed',
      });
      return false;
    }
  };

  const uploadAll = async () => {
    const pending = queue.filter((i) => i.status === 'queued' || i.status === 'error');
    if (pending.length === 0) {
      toast.info('Nothing to upload');
      return;
    }
    setUploadingAll(true);
    let ok = 0;
    // Sequential to keep server pressure predictable and progress ordered.
    for (const item of pending) {
      const success = await uploadOne(item);
      if (success) ok += 1;
    }
    setUploadingAll(false);
    if (ok > 0) toast.success(`${ok} riddim${ok > 1 ? 's' : ''} added to the shared library`);
    load();
  };

  const openEdit = (inst) => {
    setEditingInst(inst);
    setEditForm({
      title: inst.title || '',
      genre: inst.genre || '',
      bpm: inst.bpm ? String(inst.bpm) : '',
      musical_key: inst.musical_key || '',
      tags: (inst.tags || []).join(', '),
    });
  };

  const saveEdit = async () => {
    if (!editingInst) return;
    if (!editForm.title.trim()) {
      toast.error('Title cannot be empty');
      return;
    }
    setSavingEdit(true);
    try {
      await api.adminUpdateInstrumental(editingInst.id, {
        title: editForm.title.trim(),
        genre: editForm.genre || null,
        bpm: editForm.bpm ? Number(editForm.bpm) : null,
        musical_key: editForm.musical_key || null,
        tags: editForm.tags,
      });
      toast.success('Riddim updated');
      setEditingInst(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (inst) => {
    if (!window.confirm(`Delete "${inst.title}" from the library?`)) return;
    try {
      await api.deleteInstrumental(inst.id);
      toast.success('Deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return instrumentals.filter((i) => {
      if (genreFilter !== 'all' && (i.genre || '') !== genreFilter) return false;
      if (!q) return true;
      const hay = [
        i.title, i.uploaded_by, i.musical_key,
        i.bpm ? String(i.bpm) : '',
        ...(i.tags || []),
      ].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [instrumentals, search, genreFilter]);

  const shared = filtered.filter((i) => !i.artist_id);
  const artistOwned = filtered.filter((i) => i.artist_id);
  const rows = [...shared, ...artistOwned];

  const pendingCount = queue.filter((i) => i.status === 'queued' || i.status === 'error').length;
  const anyInProgress = queue.some((i) => i.status === 'uploading');

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-lime" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="studio-library-tab">
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Music2 className="w-5 h-5 text-lime" />Studio Instrumental Library
        </h2>
        <p className="text-xs text-[#888] mt-1">
          Riddims uploaded here are visible to ALL artists in the Studio. Tag them with genre / BPM / key
          so the artist roster has an easy day-one library. Tip: 320kbps MP3s are ~10× smaller than WAV
          with the same recording quality.
        </p>
      </div>

      {/* ============ UPLOAD ZONE ============ */}
      <div className="bg-surface border border-[#262626] rounded-xl p-4 space-y-4">
        <div
          onDrop={handleDrop}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onClick={() => fileRef.current?.click()}
          className={`cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
            dragActive ? 'border-lime bg-lime/5' : 'border-[#333] hover:border-lime/50'
          }`}
          data-testid="admin-instrumental-dropzone"
        >
          <UploadCloud className="w-8 h-8 mx-auto text-lime mb-2" />
          <p className="text-sm">Drop audio files here, or <span className="text-lime underline">browse</span></p>
          <p className="text-[11px] text-[#666] mt-1">MP3 / WAV / M4A / OGG · up to 40MB each</p>
          <input
            ref={fileRef}
            type="file"
            accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac,.aif,.aiff"
            multiple
            className="hidden"
            onChange={handlePick}
            onClick={(e) => e.stopPropagation()}
            data-testid="admin-instrumental-file-input"
          />
        </div>

        {/* Batch defaults form */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <Label className="text-[11px] text-[#888]">Batch genre</Label>
            <GenreSelect
              value={batchDefaults.genre}
              onChange={(v) => setBatchDefaults((p) => ({ ...p, genre: v }))}
              testid="batch-genre-select"
            />
          </div>
          <div>
            <Label className="text-[11px] text-[#888]">Batch BPM</Label>
            <Input
              type="number"
              min="20"
              max="300"
              value={batchDefaults.bpm}
              onChange={(e) => setBatchDefaults((p) => ({ ...p, bpm: e.target.value }))}
              placeholder="e.g. 90"
              className="input-dark h-9 text-xs"
              data-testid="batch-bpm-input"
            />
          </div>
          <div>
            <Label className="text-[11px] text-[#888]">Batch key</Label>
            <Input
              value={batchDefaults.musical_key}
              onChange={(e) => setBatchDefaults((p) => ({ ...p, musical_key: e.target.value }))}
              placeholder="e.g. Am, F#"
              className="input-dark h-9 text-xs"
              data-testid="batch-key-input"
            />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[11px] text-[#888]">Batch tags (comma-separated)</Label>
            <Input
              value={batchDefaults.tags}
              onChange={(e) => setBatchDefaults((p) => ({ ...p, tags: e.target.value }))}
              placeholder="one drop, love, party"
              className="input-dark h-9 text-xs"
              data-testid="batch-tags-input"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 items-center justify-between">
          <p className="text-[11px] text-[#666]">
            Defaults auto-fill new files added to the queue. Use <span className="text-white">Apply to all</span> to
            push them onto items already queued. Every row can still be overridden below.
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className="border-[#333] h-8 text-xs"
              disabled={queue.length === 0}
              onClick={applyBatchToAll}
              data-testid="apply-batch-defaults-btn"
            >
              Apply to all
            </Button>
            <Button
              size="sm"
              className="btn-primary h-8 text-xs"
              disabled={pendingCount === 0 || uploadingAll}
              onClick={uploadAll}
              data-testid="upload-all-btn"
            >
              {uploadingAll ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <UploadCloud className="w-3.5 h-3.5 mr-1.5" />}
              Upload {pendingCount > 0 ? `${pendingCount} file${pendingCount > 1 ? 's' : ''}` : ''}
            </Button>
          </div>
        </div>

        {/* Queue */}
        {queue.length > 0 && (
          <div className="border border-[#262626] rounded-lg overflow-hidden" data-testid="upload-queue">
            <div className="flex items-center justify-between px-3 py-2 bg-[#0e0e0e] border-b border-[#262626]">
              <span className="text-xs font-semibold">Queue · {queue.length} file{queue.length > 1 ? 's' : ''}</span>
              <button
                onClick={clearFinished}
                className="text-[11px] text-[#888] hover:text-white"
                disabled={anyInProgress}
                data-testid="clear-finished-btn"
              >
                Clear finished
              </button>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {queue.map((item) => (
                <QueueRow
                  key={item.fileId}
                  item={item}
                  onChange={(u) => updateQueueItem(item.fileId, u)}
                  onRemove={() => removeFromQueue(item.fileId)}
                  onRetry={() => uploadOne(item)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ============ EXISTING LIBRARY ============ */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search title, tag, key or BPM…"
              className="input-dark pl-9 h-9 text-xs"
              data-testid="library-search-input"
            />
          </div>
          <select
            value={genreFilter}
            onChange={(e) => setGenreFilter(e.target.value)}
            className="input-dark h-9 text-xs px-2 rounded-md"
            data-testid="library-genre-filter"
          >
            <option value="all">All genres</option>
            {GENRES.filter(g => g.value).map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
          </select>
          <span className="text-[11px] text-[#666]" data-testid="library-count">
            {rows.length} of {instrumentals.length} shown
          </span>
        </div>

        <div className="bg-surface border border-[#262626] rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#262626] text-left">
                <th className="p-3 text-xs text-[#A1A1AA]">Title</th>
                <th className="p-3 text-xs text-[#A1A1AA]">Genre</th>
                <th className="p-3 text-xs text-[#A1A1AA]">BPM</th>
                <th className="p-3 text-xs text-[#A1A1AA]">Key</th>
                <th className="p-3 text-xs text-[#A1A1AA]">Tags</th>
                <th className="p-3 text-xs text-[#A1A1AA]">Scope</th>
                <th className="p-3 text-xs text-[#A1A1AA]">Preview</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {rows.map((inst) => (
                <tr key={inst.id} className="border-b border-[#1a1a1a] align-top" data-testid={`admin-instrumental-row-${inst.id}`}>
                  <td className="p-3">
                    <div className="text-sm">{inst.title}</div>
                    <div className="text-[10px] text-[#666]">{fmtSize(inst.size)}</div>
                  </td>
                  <td className="p-3 text-xs">
                    {inst.genre ? <Badge className="bg-[#222] text-[#e4e4e4] border-0">{genreLabel(inst.genre)}</Badge> : <span className="text-[#666]">—</span>}
                  </td>
                  <td className="p-3 text-xs font-mono text-[#e4e4e4]">{inst.bpm || <span className="text-[#666]">—</span>}</td>
                  <td className="p-3 text-xs font-mono text-[#e4e4e4]">{inst.musical_key || <span className="text-[#666]">—</span>}</td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1 max-w-[220px]">
                      {(inst.tags || []).length === 0 && <span className="text-[#666] text-xs">—</span>}
                      {(inst.tags || []).map((t) => (
                        <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-lime/10 text-lime border border-lime/20">{t}</span>
                      ))}
                    </div>
                  </td>
                  <td className="p-3">
                    <Badge className={inst.artist_id ? 'bg-blue-500/20 text-blue-400' : 'bg-lime/20 text-lime'}>
                      {inst.artist_id ? `Artist: ${inst.uploaded_by}` : 'Shared'}
                    </Badge>
                  </td>
                  <td className="p-3">
                    <audio controls preload="none" src={`${BACKEND_URL}${inst.url}`} className="h-8 max-w-[200px]" />
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => openEdit(inst)}
                      className="text-[#888] hover:text-lime p-1"
                      title="Edit metadata"
                      data-testid={`edit-instrumental-${inst.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(inst)}
                      className="text-[#666] hover:text-red-400 p-1 ml-1"
                      title="Delete"
                      data-testid={`admin-delete-instrumental-${inst.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-sm text-[#666]">
                    {instrumentals.length === 0
                      ? 'No riddims yet — drop the first batch above.'
                      : 'No riddims match your filters.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ============ EDIT DIALOG ============ */}
      <Dialog open={!!editingInst} onOpenChange={(o) => { if (!o) setEditingInst(null); }}>
        <DialogContent className="bg-surface border-[#262626]" data-testid="edit-instrumental-dialog">
          <DialogHeader>
            <DialogTitle>Edit Riddim</DialogTitle>
            <DialogDescription>Update the metadata artists see in the Studio library.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-[#888]">Title</Label>
              <Input
                value={editForm.title}
                onChange={(e) => setEditForm((p) => ({ ...p, title: e.target.value }))}
                className="input-dark"
                data-testid="edit-title-input"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-[#888]">Genre</Label>
                <GenreSelect
                  value={editForm.genre}
                  onChange={(v) => setEditForm((p) => ({ ...p, genre: v }))}
                  testid="edit-genre-select"
                />
              </div>
              <div>
                <Label className="text-xs text-[#888]">BPM</Label>
                <Input
                  type="number"
                  min="20"
                  max="300"
                  value={editForm.bpm}
                  onChange={(e) => setEditForm((p) => ({ ...p, bpm: e.target.value }))}
                  className="input-dark h-9 text-xs"
                  data-testid="edit-bpm-input"
                />
              </div>
              <div>
                <Label className="text-xs text-[#888]">Key</Label>
                <Input
                  value={editForm.musical_key}
                  onChange={(e) => setEditForm((p) => ({ ...p, musical_key: e.target.value }))}
                  className="input-dark h-9 text-xs"
                  data-testid="edit-key-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-[#888]">Tags (comma-separated)</Label>
              <Input
                value={editForm.tags}
                onChange={(e) => setEditForm((p) => ({ ...p, tags: e.target.value }))}
                className="input-dark h-9 text-xs"
                placeholder="one drop, love, party"
                data-testid="edit-tags-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingInst(null)} className="border-[#262626]">Cancel</Button>
            <Button onClick={saveEdit} disabled={savingEdit} className="btn-primary" data-testid="save-edit-btn">
              {savingEdit ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function QueueRow({ item, onChange, onRemove, onRetry }) {
  const disabled = item.status === 'uploading' || item.status === 'done';
  const previewUrl = useMemo(() => URL.createObjectURL(item.file), [item.file]);
  useEffect(() => () => URL.revokeObjectURL(previewUrl), [previewUrl]);

  return (
    <div className="p-3 border-b border-[#1a1a1a] last:border-b-0 space-y-2" data-testid={`queue-row-${item.fileId}`}>
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded bg-[#111] border border-[#222] flex items-center justify-center shrink-0">
          <Play className="w-3.5 h-3.5 text-lime" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-xs text-[#888] truncate">{item.file.name}</p>
            <span className="text-[10px] text-[#555]">{fmtSize(item.file.size)}</span>
          </div>
          {item.status === 'uploading' && (
            <div className="mt-1 h-1 bg-[#222] rounded overflow-hidden">
              <div className="h-full bg-lime transition-all" style={{ width: `${item.progress}%` }} data-testid={`queue-progress-${item.fileId}`} />
            </div>
          )}
          {item.status === 'done' && (
            <p className="text-[10px] text-lime flex items-center gap-1 mt-0.5"><CheckCircle2 className="w-3 h-3" />Uploaded</p>
          )}
          {item.status === 'error' && (
            <p className="text-[10px] text-red-400 flex items-center gap-1 mt-0.5"><AlertCircle className="w-3 h-3" />{item.error}</p>
          )}
        </div>
        <audio controls preload="none" src={previewUrl} className="h-7 max-w-[160px] hidden md:block" />
        {item.status === 'error' && (
          <Button size="sm" variant="outline" className="border-[#333] h-7 text-xs" onClick={onRetry} data-testid={`queue-retry-${item.fileId}`}>
            Retry
          </Button>
        )}
        <button
          onClick={onRemove}
          className="text-[#666] hover:text-red-400 p-1 shrink-0"
          disabled={item.status === 'uploading'}
          title="Remove"
          data-testid={`queue-remove-${item.fileId}`}
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Input
          value={item.title}
          onChange={(e) => onChange({ title: e.target.value })}
          className="input-dark h-8 text-xs md:col-span-2"
          placeholder="Title"
          disabled={disabled}
          data-testid={`queue-title-${item.fileId}`}
        />
        <GenreSelect
          value={item.genre}
          onChange={(v) => onChange({ genre: v })}
          testid={`queue-genre-${item.fileId}`}
        />
        <Input
          type="number"
          min="20"
          max="300"
          value={item.bpm}
          onChange={(e) => onChange({ bpm: e.target.value })}
          className="input-dark h-8 text-xs"
          placeholder="BPM"
          disabled={disabled}
          data-testid={`queue-bpm-${item.fileId}`}
        />
        <Input
          value={item.musical_key}
          onChange={(e) => onChange({ musical_key: e.target.value })}
          className="input-dark h-8 text-xs"
          placeholder="Key"
          disabled={disabled}
          data-testid={`queue-key-${item.fileId}`}
        />
      </div>
      <Input
        value={item.tags}
        onChange={(e) => onChange({ tags: e.target.value })}
        className="input-dark h-8 text-xs"
        placeholder="Tags (comma-separated)"
        disabled={disabled}
        data-testid={`queue-tags-${item.fileId}`}
      />
    </div>
  );
}
